/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import DevicesService from 'api/devices/DevicesService';
import { DeviceDto } from 'api/devices/models/DeviceDto';
import UsersService from 'api/users/UsersService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useRef, useState } from 'react';
import { Breadcrumb, Button, Col, Form, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './DeviceScreen.module.scss';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import SearchableSelect from 'common/components/searchableSelect/SearchableSelect';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import CompaniesService from 'api/companies/CompaniesService';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import DeviceBrandsService from 'api/deviceBrands/DeviceBrandsService';
import SimCardsService from 'api/simCards/SimCardsService';
import DeviceModelsService from 'api/deviceModels/DeviceModelsService';

type Props = {
};

const DeviceScreen: React.FC<Props> = () => {
    const { id, type } = useParams<{ id: string, type: string }>();
    const [isDetails, setIsDetails] = useState<boolean>(type === 'details');
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasDevicesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['CONFIG_DEVICES_WRITE']);
    const hasDevicesRestrictWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['MY_DEVICES_WRITE']);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<DeviceDto | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [loading, setLoading] = useState(false);
    const prevCompanyIdRef = useRef<string>('');
    const prevBrandIdRef = useRef<string>('');
    const [item, setItem] = useState<DeviceDto>();
    const [companies, setCompanies] = useState<SelectValueLabel[]>([]);
    const [brands, setBrands] = useState<SelectValueLabel[]>([]);
    const [models, setModels] = useState<SelectValueLabel[]>([]);
    const [simCards, setSimCards] = useState<SelectValueLabel[]>([]);
    const [simCardsById, setSimCardsById] = useState<Record<string, SelectValueLabel>>({});
    const [simCardConflictMessage, setSimCardConflictMessage] = useState<string | undefined>(undefined);
    const [simCardInactiveWarning, setSimCardInactiveWarning] = useState<string | undefined>(undefined);
    const [refresh, setRefresh] = useState<number>(1);
    const [formData, setFormData] = useState({
        id: '',
        code: '',
        companyId: '',
        deviceBrandId: '',
        deviceModelId: '',
        simCardId: '',
        rowVersion: ''
    });

    const applySimCardOptions = (simCardsRaw: SelectValueLabel[], currentSimCardId?: string) => {
        const byId: Record<string, SelectValueLabel> = {};
        simCardsRaw.forEach(s => { if (s.value) byId[s.value] = s; });
        setSimCardsById(byId);

        const visible = simCardsRaw
            .filter(s => !s.assignedToActive || s.value === currentSimCardId)
            .map(s => ({
                ...s,
                label: s.assignedToInactive
                    ? `${s.label} (${t('vehicles.inactive')})`
                    : s.label
            }));
        setSimCards(visible);
    };

    const getData = async () => {
        try {
            setLoading(true);
            let result: any = { id: '', code: '', companyId: '' };
            Loading.show();
            const companies = await CompaniesService.getCompanies();
            setCompanies(companies);
            const brands = await DeviceBrandsService.getDeviceBrands();
            setBrands(brands);
            if (id) {
                result = await DevicesService.getById(id);
                const smartCards = await SimCardsService.getSimCards(result.companyId ?? '', id);
                applySimCardOptions(smartCards ?? [], result.simCardId);
                const models = await DeviceModelsService.getDeviceModels(result.deviceBrandId ?? '');
                setModels(models ?? []);
            }
            setItem(result);
            setFormData(result);
            prevCompanyIdRef.current = result.companyId ?? '';
            prevBrandIdRef.current = result.deviceBrandId ?? '';
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get device information', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            setLoading(false);
            Loading.hide();
            setRefresh(refresh + 1);
        }
    };

    useEffect(() => {
        getData().catch(console.error);
    }, [id, type]);

    useEffect(() => {
        if (loading || formData.deviceBrandId === prevBrandIdRef.current) {
            prevBrandIdRef.current = '';
            return;
        }
        if (formData.deviceBrandId) {
            DeviceModelsService
                .getDeviceModels(formData.deviceBrandId ?? '')
                .then(data => setModels(data));
        } else {
            setModels([]);
        }
    }, [formData.deviceBrandId]);

    useEffect(() => {
        if (loading || formData.companyId === prevCompanyIdRef.current) {
            prevCompanyIdRef.current = '';
            return;
        }
        if (formData.companyId) {
            SimCardsService
                .getSimCards(formData.companyId ?? '', id)
                .then(data => applySimCardOptions(data, formData.simCardId));
        } else {
            setSimCards([]);
            setSimCardsById({});
        }
    }, [formData.companyId]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFormData({ ...formData, [key]: value });

        if (key === 'simCardId') {
            setSimCardConflictMessage(undefined);
            const sc = value ? simCardsById[value] : undefined;
            if (sc?.assignedToInactive && value !== item?.simCardId) {
                setSimCardInactiveWarning(t('devices.sim_card_in_use_inactive', { name: sc.assignedToDeviceName ?? '' }) ?? undefined);
            } else {
                setSimCardInactiveWarning(undefined);
            }
        }
    }

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/device/${typeUrl}/${id}`);
            setIsDetails(typeUrl === 'details');
        } else {
            navigate('/devices');
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }

        try {
            await DevicesService.remove(itemToRemove);
            onCancelRemove();
            navigateTo();
            toast.success(t('messages.record_delete_success'));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete device', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const showRemoveItemDialog = async (item: DeviceDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSubmit = async (event: any) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);
        if (form.checkValidity() === false) {
            return;
        }
        try {
            Loading.show();
            const model: DeviceDto = {
                id: formData.id,
                code: formData.code,
                companyId: formData.companyId,
                deviceBrandId: formData.deviceBrandId,
                deviceModelId: formData.deviceModelId,
                simCardId: formData.simCardId,
                rowVersion: formData.rowVersion
            };
            if (model && model.id) {
                await DevicesService.update(model)
                navigateTo('details', id);
            } else if (model) {
                const id = await DevicesService.create(model)
                navigateTo('details', id);
            }
            Loading.hide();
            toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            if (error?.response?.status === 409) {
                if (error?.response?.data?.code === 'SIM_CARD_IN_USE') {
                    const message = error.response.data.message as string;
                    setSimCardConflictMessage(message);
                    toast(message);
                } else {
                    toast(t('messages.device_already_exists'));
                }
            } else {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the device with id: ${form.id}`, error);
                toast.error(t('messages.error_load_info'));
            }
            Loading.hide();
        }
    };

    return (
        <ScreenTitle title={t('devices.title')}>
            <Breadcrumb>
                <Breadcrumb.Item onClick={() => { navigateTo() }}>
                    {t('devices.title')}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{t(`common.${type}`)}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="bg-white rounded-2xl border shadow-sm p-4" style={{ borderColor: '#e2e8f0' }}>
            <Form onSubmit={onSubmit} noValidate validated={validated} key={refresh}>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="code">
                            <Form.Label>{t('devices.code')}{!isDetails ? '*' : ''}</Form.Label>
                            <Form.Control required name="code" type="text" defaultValue={item?.code} readOnly={isDetails || !hasDevicesWritePolicy} onChange={handleChange} placeholder={t('devices.code')} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="companyId">
                            <Form.Label>{t('devices.company')}{!isDetails ? '*' : ''}</Form.Label>
                            <SearchableSelect
                                readOnly={isDetails || !hasDevicesWritePolicy}
                                required
                                name="companyId"
                                value={formData.companyId}
                                options={companies}
                                placeholder={t('common.select_option') ?? ''}
                                onChange={handleChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="deviceBrandId">
                            <Form.Label>{t('devices.brand')}</Form.Label>
                            <SearchableSelect
                                readOnly={isDetails || !hasDevicesWritePolicy}
                                name="deviceBrandId"
                                value={formData.deviceBrandId}
                                options={brands}
                                placeholder={t('common.select_option') ?? ''}
                                onChange={handleChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="deviceModelId">
                            <Form.Label>{t('devices.model')}</Form.Label>
                            <SearchableSelect
                                readOnly={isDetails || !hasDevicesWritePolicy}
                                name="deviceModelId"
                                value={formData.deviceModelId}
                                options={models}
                                placeholder={t('common.select_option') ?? ''}
                                onChange={handleChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="simCardId">
                            <Form.Label>{t('devices.sim_card')}</Form.Label>
                            <SearchableSelect
                                readOnly={isDetails}
                                name="simCardId"
                                value={formData.simCardId}
                                options={simCards}
                                placeholder={t('common.select_option') ?? ''}
                                onChange={handleChange}
                            />
                            {!!simCardConflictMessage && <Form.Text className="text-danger">{simCardConflictMessage}</Form.Text>}
                            {!simCardConflictMessage && !!simCardInactiveWarning && <Form.Text className="text-warning">{simCardInactiveWarning}</Form.Text>}
                        </Form.Group>
                    </Col>
                </Row>
                <div className={styles.buttonsContainer}>
                    <Button variant="secondary" type="button" className={styles.button} onClick={() => navigateTo()}>
                        {t('common.cancel')}
                    </Button>
                    {isDetails && hasDevicesWritePolicy &&
                        <Button variant="danger" type="button" className={styles.button} onClick={() => showRemoveItemDialog(item as DeviceDto)} >
                            {t('common.delete')}
                        </Button>
                    }
                    {isDetails && (hasDevicesWritePolicy || hasDevicesRestrictWritePolicy) &&
                        <Button variant="warning" type="button" className={styles.button} onClick={() => navigateTo('edit', item?.id)}>
                            {t('common.edit')}
                        </Button>
                    }
                    {!isDetails && (hasDevicesWritePolicy || hasDevicesRestrictWritePolicy) &&
                        <Button variant="primary" type="submit" className={styles.button}>
                            {t('common.save')}
                        </Button>
                    }
                </div>
            </Form>
            </div>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: item?.code ?? '' })} />
        </ScreenTitle>
    );
};

export default DeviceScreen;
